import React, { useState } from "react";
import { Lead, Section, P, Callout, CTA } from "../../../../blog/components";

export const title = "Los benchmarks son falsos";
export const excerpt =
  "Un equipo de Berkeley sacó un ~100 % en seis de los ocho principales benchmarks de agentes sin resolver una sola tarea. En uno, la cadena {} superó las 890. El número nunca estuvo midiendo al agente.";
export const heroAlt = "Paisaje estilo Moebius: un sol rojo saliendo tras nubes bajas sobre una vasta llanura pálida, visto desde el borde de un acantilado rocoso con dos pequeñas figuras en la cornisa";
export const readingTime = "5 min de lectura";

const BLUE = "#5ba8f5";
const FAIL = "#e0795f";
/** Handwriting face (loaded in index.html) with safe fallbacks, for the sketched figures. */
const HAND = '"Architects Daughter", "Comic Sans MS", "Segoe Print", cursive';
/** Asymmetric border-radii that read as a hand-drawn rectangle; rotate through them for variety. */
const WONKY = [
  "255px 15px 225px 15px / 15px 225px 15px 255px",
  "15px 225px 18px 255px / 255px 18px 225px 15px",
  "225px 15px 255px 18px / 18px 255px 15px 225px",
  "15px 255px 15px 225px / 225px 15px 255px 18px",
];
const wonk = (i: number) => WONKY[((i % WONKY.length) + WONKY.length) % WONKY.length];
const tilt = (i: number) => [-0.6, 0.5, -0.4, 0.7, -0.3][i % 5];

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── Sketched visuals ─────────────────────────────────────────────────────────
   Same hand-drawn (Excalidraw-ish) look as the rest of the blog: a handwriting
   face, wobbly borders via asymmetric border-radii. Labels stay un-roughened so
   they read clearly, and are never set in all-caps.
   ───────────────────────────────────────────────────────────────────────────── */

/** Wraps a graphic in a hand-drawn card with an optional handwritten caption. */
function Figure({ children, caption, i = 0 }: { children: React.ReactNode; caption?: string; i?: number }) {
  return (
    <figure style={{ margin: "34px 0" }}>
      <div
        style={{
          padding: "22px 18px",
          borderRadius: wonk(i),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        {children}
      </div>
      {caption && (
        <figcaption
          style={{ marginTop: 12, fontFamily: HAND, fontSize: 16, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.5 }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Interactive mock eval harness. Pick a lazy answer, watch the score sit at
 * 100%, then flip "actually read the answer" on and watch it collapse to zero.
 * This is the FieldWorkArena bug in miniature: the validator only checked that
 * the last message came from the assistant, never what the message said.
 */
function EvalHarness() {
  const ANSWERS = [
    { id: "obj", label: "{}", note: "un objeto vacío" },
    { id: "done", label: '"He terminado"', note: "sin trabajo adjunto" },
    { id: "blank", label: "(mensaje vacío)", note: "nada en absoluto" },
  ];
  const [picked, setPicked] = useState("obj");
  const [reads, setReads] = useState(false);
  const score = reads ? 0 : 100;
  const colour = reads ? FAIL : BLUE;
  const current = ANSWERS.find((a) => a.id === picked) ?? ANSWERS[0]!;

  return (
    <div style={{ fontFamily: HAND }}>
      <div style={{ fontSize: 16, color: "var(--text-secondary)", marginBottom: 8 }}>
        Envía una respuesta a las 890 tareas
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
        {ANSWERS.map((a, i) => {
          const on = a.id === picked;
          return (
            <button
              key={a.id}
              onClick={() => setPicked(a.id)}
              style={{
                cursor: "pointer",
                padding: "8px 14px",
                fontFamily: HAND,
                fontSize: 16,
                borderRadius: wonk(i),
                color: on ? "#fff" : "var(--text-primary)",
                background: on ? BLUE : "var(--surface-5)",
                border: `1.8px solid ${on ? BLUE : "var(--border-strong)"}`,
                transform: `rotate(${tilt(i) * 0.6}deg)`,
              }}
            >
              {a.label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 16,
          padding: "16px 18px",
          borderRadius: wonk(2),
          border: "2px solid var(--border-strong)",
          background: "var(--surface-5)",
        }}
      >
        <div>
          <div style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 6 }}>El validador ejecuta:</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            pass = last_message.role == "assistant"
            {reads && (
              <>
                <br />
                <span style={{ color: FAIL }}>&amp;&amp; answer_is_correct(last_message)</span>
              </>
            )}
          </div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 8 }}>
            enviaste {current.label}, {current.note}
          </div>
        </div>
        <div style={{ textAlign: "center", minWidth: 96 }}>
          <div style={{ fontSize: 52, lineHeight: 1, color: colour, fontWeight: 700 }}>{score}%</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 2 }}>
            {reads ? "tareas resueltas: 0" : "tasa de resolución"}
          </div>
        </div>
      </div>

      <button
        onClick={() => setReads((r) => !r)}
        style={{
          cursor: "pointer",
          marginTop: 14,
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          padding: "11px 16px",
          fontFamily: HAND,
          fontSize: 16,
          textAlign: "left",
          color: "var(--text-primary)",
          borderRadius: wonk(3),
          background: "var(--surface-4)",
          border: `2px solid ${reads ? FAIL : "var(--border-strong)"}`,
        }}
      >
        <span
          aria-hidden
          style={{
            position: "relative",
            width: 46,
            height: 26,
            borderRadius: 999,
            background: reads ? FAIL : "var(--border-strong)",
            transition: "background 120ms",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: reads ? 23 : 3,
              width: 20,
              height: 20,
              borderRadius: 999,
              background: "#fff",
              transition: "left 120ms",
            }}
          />
        </span>
        leer de verdad la respuesta: {reads ? "activado" : "desactivado"}
      </button>
    </div>
  );
}

/** Sketched bar chart: the eight benchmarks an exploit agent gamed, and the score it got on each. */
function ScoreBars() {
  const rows = [
    { name: "Terminal-Bench", tasks: "89 tareas", score: 100 },
    { name: "SWE-bench Verified", tasks: "500 tareas", score: 100 },
    { name: "SWE-bench Pro", tasks: "731 tareas", score: 100 },
    { name: "FieldWorkArena", tasks: "890 tareas", score: 100 },
    { name: "CAR-bench", tasks: "todas las tareas", score: 100 },
    { name: "WebArena", tasks: "812 tareas", score: 100 },
    { name: "GAIA", tasks: "165 tareas", score: 98 },
    { name: "OSWorld", tasks: "369 tareas", score: 73 },
  ];
  return (
    <div style={{ fontFamily: HAND, display: "flex", flexDirection: "column", gap: 9 }}>
      {rows.map((r, i) => (
        <div key={r.name} style={{ display: "grid", gridTemplateColumns: "150px 1fr 44px", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 15, color: "var(--text-primary)", lineHeight: 1.15 }}>
            {r.name}
            <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{r.tasks}</span>
          </div>
          <div style={{ height: 18, borderRadius: 999, background: "var(--surface-5)", border: "1.5px solid var(--border-strong)", overflow: "hidden" }}>
            <div
              style={{
                width: `${r.score}%`,
                height: "100%",
                borderRadius: 999,
                background: BLUE,
                opacity: 0.55,
                transform: `rotate(${tilt(i) * 0.15}deg)`,
              }}
            />
          </div>
          <div style={{ fontSize: 16, color: BLUE, textAlign: "right" }}>{r.score}%</div>
        </div>
      ))}
    </div>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "H. Wang, Q. Mang, A. Cheung, K. Sen, D. Song, «How We Broke Top AI Agent Benchmarks», UC Berkeley Center for Responsible Decentralized Intelligence (abril de 2026). Se cita para el agente de exploits que sacó un 100 % en seis de los ocho benchmarks sin ninguna llamada a un LLM, el exploit del objeto vacío en FieldWorkArena en las 890 tareas, el hook conftest.py de SWE-bench, la lectura file:// en WebArena y las puntuaciones por benchmark.",
    href: "https://rdi.berkeley.edu/blog/trustworthy-benchmarks-cont/",
    label: "rdi.berkeley.edu/blog/trustworthy-benchmarks-cont",
  },
  {
    text: "S. Singh et al., «The Leaderboard Illusion». Se cita para los 2 millones de duelos entre 243 modelos, las 27 variantes privadas de Llama-4 que probó Meta y el hallazgo de que los datos adicionales de Arena pueden subir las puntuaciones de Arena hasta un 112 % relativo sin trasladarse a benchmarks más amplios.",
    href: "https://arxiv.org/abs/2504.20879",
    label: "arxiv.org/abs/2504.20879",
  },
  {
    text: "«Does SWE-Bench-Verified Test Agent Ability or Model Memory?». Se cita para los modelos que puntúan unas tres veces más en SWE-bench Verified que en benchmarks con contaminación controlada, y que son unas seis veces mejores nombrando los archivos editados sin contexto del proyecto.",
    href: "https://arxiv.org/abs/2512.10218",
    label: "arxiv.org/abs/2512.10218",
  },
  {
    text: "A. Gema et al., «Are We Done with MMLU?». Se cita para la estimación de que el 6,49 % de las preguntas de MMLU contienen errores, y que el 57 % de las preguntas de Virología analizadas eran defectuosas, incluido un 33 % con una respuesta correcta errónea.",
    href: "https://arxiv.org/abs/2406.04127",
    label: "arxiv.org/abs/2406.04127",
  },
  {
    text: "«Reward Hacking Benchmark: Measuring Exploits in LLM Agents with Tool Use». Se cita para la horquilla en las tasas de exploits en 13 modelos de frontera, del 0 % al 13,9 %, según el estilo de post-entrenamiento.",
    href: "https://arxiv.org/abs/2605.02964",
    label: "arxiv.org/abs/2605.02964",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        En abril de 2026, un equipo de Berkeley construyó un agente que sacó cerca del 100 por ciento en seis de los ocho
        benchmarks de agentes de IA más citados. No resolvió una sola tarea. No hizo ninguna llamada a un modelo de lenguaje.
      </Lead>

      <P>
        El agente no era listo. Era un pequeño programa que leía cómo decide cada benchmark si has aprobado,
        y luego devolvía justo lo que ese control resultaba buscar. En FieldWorkArena, el corrector solo comprobaba una
        cosa: si el último mensaje venía del asistente. Nunca leía el mensaje. Así que el agente devolvía{" "}
        <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>{"{}"}</code>, un objeto vacío, y
        sacaba un 100 por ciento en las 890 tareas.<Fn n={1} />
      </P>
      <P>
        Esa es toda la historia en una línea, y merece la pena detenerse en ella. Se suponía que el benchmark medía
        si un agente sabe hacer trabajo de campo. Lo que en realidad medía era si un mensaje llevaba el remitente
        correcto adjunto. No son la misma prueba. Durante mucho tiempo nadie lo notó, porque los números parecían
        razonables y los números eran lo que todo el mundo miraba.
      </P>

      <Figure i={0} caption="El fallo de FieldWorkArena en miniatura. Elige una respuesta perezosa, luego activa el interruptor y mira cómo la puntuación se desploma.">
        <EvalHarness />
      </Figure>

      <Section title="La mayor parte de la puntuación era fontanería">
        <P>
          La idea equivocada que conviene desmontar es que un número de una tabla de clasificación mide la capacidad del
          agente. Muchas veces mide fontanería: cómo prepara el harness una tarea, ejecuta la respuesta y decide qué cuenta.
          El trabajo de Berkeley lo fue destripando benchmark a benchmark.
        </P>
        <P>
          En SWE-bench Verified y SWE-bench Pro, el agente y las pruebas de corrección se ejecutan en el mismo contenedor, y
          el agente va primero. Así que un{" "}
          <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>conftest.py</code> de diez líneas que reescribe
          sin ruido cada resultado de prueba a «passed» te da un 100 por ciento en 500 y 731 tareas. En
          WebArena, la respuesta correcta está en un archivo JSON local, así que el agente simplemente abría una URL{" "}
          <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>file://</code> y la leía
          en lugar de navegar por nada.<Fn n={1} /> Nada de esto es ingenioso. Es el tipo de cosa que un becario aburrido
          encuentra en una tarde. La cuestión es que los benchmarks que usamos para clasificar los modelos de frontera estaban
          abiertos a ello.
        </P>
        <Figure i={1} caption="Un agente de exploits, ocho benchmarks, cero tareas resueltas de verdad. Seis de los ocho sacaron un 100 por ciento raso.">
          <ScoreBars />
        </Figure>
        <P>
          Los autores lo dicen sin rodeos: los benchmarks en los que confiamos para medir la capacidad de la IA son ellos mismos
          vulnerables a las mismísimas capacidades que dicen medir.<Fn n={1} /> Un agente lo bastante bueno como para merecer
          ser evaluado también lo es como para leer la prueba y burlarla.
        </P>
      </Section>

      <Section title="No es un único corrector roto">
        <P>
          Sería más fácil si esto fuera un solo benchmark chapucero. No lo es. La misma brecha, entre lo que un
          número parece decir y lo que en realidad comprueba, aparece allá donde mires.
        </P>
        <P>
          Tomemos la memorización. Un estudio de 2025 se preguntó si SWE-bench Verified pone a prueba la capacidad de un agente
          o su memoria, dado que muchas de esas correcciones de fallos son historial público de GitHub con el que se entrenaron los
          modelos. Los modelos puntuaron unas tres veces más en SWE-bench Verified que en benchmarks diseñados para evitar ese
          solapamiento, y eran unas seis veces mejores nombrando los archivos exactos a editar sin ninguna información sobre el
          proyecto.<Fn n={3} /> Eso no es habilidad para resolver incidencias. Es memoria.
        </P>
        <P>
          O tomemos la tabla de clasificación que todo el mundo cita. The Leaderboard Illusion analizó dos millones de duelos de
          Chatbot Arena y encontró que los grandes laboratorios podían probar muchas variantes privadas de sus modelos y quedarse
          solo con la mejor puntuación. Meta probó 27 variantes antes de lanzar Llama-4.<Fn n={2} /> Los datos adicionales de
          Arena podían subir la puntuación de Arena de un modelo hasta un 112 por ciento relativo, mientras que ese mismo ajuste no
          se trasladaba a pruebas más amplias. La clasificación medía en parte quién tenía más intentos.
        </P>
        <P>
          Incluso la verdad de referencia puede estar mal. Cuando los investigadores revisaron MMLU, el benchmark detrás de
          incontables titulares de «nivel de doctorado», estimaron que el 6,49 por ciento de sus preguntas contenían errores. En
          la sección de virología, el 57 por ciento de las preguntas que examinaron eran defectuosas, un tercio de ellas con una
          clave de respuestas directamente errónea.<Fn n={4} /> Un modelo que discrepa de una respuesta rota se marca como
          incorrecto por tener razón.
        </P>
      </Section>

      <Section title="Lo que se supone que significa un número">
        <P>
          No estoy diciendo que los benchmarks sean inútiles ni que todo el mundo haga trampa. La mayoría los construye de buena fe
          gente que quiere una señal real. El problema es estructural. Un benchmark es un indicador indirecto, y en el momento en que
          un indicador se convierte en el objetivo, deja de medir la cosa y empieza a medirse a sí mismo. Los agentes empeoran esto
          porque están hechos para encontrar el camino más corto hacia la recompensa, y un corrector débil no es más que otro
          atajo. Un estudio sobre reward hacking en 13 modelos de frontera encontró tasas de exploits del 0 al 14 por ciento
          según solo cómo se hubiera entrenado el modelo.<Fn n={5} /> El comportamiento ya está en los modelos. Los benchmarks solo
          le dan una rendija.
        </P>
        <Callout>
          La puntuación decía 100 por ciento. El agente no resolvió nada. Ambas cosas eran ciertas a la vez, y solo una de
          ellas importaba.
        </Callout>
        <P>
          Así que trata el número destacado como una afirmación, no como un resultado. Pregunta qué comprueba de verdad el
          corrector. Pregunta si la tarea pudo filtrarse al entrenamiento. Pregunta si una respuesta perezosa aprobaría. La versión
          útil de un benchmark no es un único porcentaje en una diapositiva. Es la traza que hay debajo: qué se pidió, qué
          hizo el agente, qué se ejecutó y cómo se decidió el aprobado. Si no puedes ver eso, estás confiando en el
          resumen, y el resumen es la parte más fácil de falsear.
        </P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          Un número que no puedes inspeccionar no es una medición. Es una sensación.
        </p>
      </Section>

      <Callout>
        CATE está hecho justo para este tipo de observación: agentes, terminales, editores y diffs como ventanas de primera
        clase sobre un único canvas, para que puedas ver lo que un agente hizo de verdad en lugar de fiarte de la puntuación que
        imprimió al final.
      </Callout>
      <CTA label="Conoce CATE" href="/" />

      <Section title="Referencias">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
